import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'countrySelect', 'couponRadio', 'deliveryRadio',
    'customToggle', 'sliderWrap', 'rangeInput', 'monthLabel', 'customCouponId',
    'formatMode', 'physicalWrap', 'mailDeliveryId',
    'recipientTitleDirect', 'recipientTitleSelf',
    'recipientMode',
    'fieldsDirectMail', 'fieldsSelfMail', 'fieldsDirectPhysical', 'fieldsSelfPhysical',
    'recipientEmail', 'selfEmail', 'recipientName', 'recipientFirstName', 'senderName', 'note', 'charCount', 'charStatus',
    'streetAddress', 'postalCode', 'city', 'phone',
    'selfFullName', 'selfStreetAddress', 'selfPostalCode', 'selfCity', 'selfPhone',
    'sectionDuration', 'sectionFormat', 'sectionRecipient', 'sectionMessage',
    'submitBtnDesktop', 'submitBtnMobile'
  ];

  static values = {
    recapUrl:  String,
    couponIds: Object
  };

  connect() {
    this.updateCharCount();
    this._syncRecipientFields();
    this._progressReveal();
    this.updateRecap();
    this.validate();

    this._onFrameLoad = () => this.validate();
    document.addEventListener('turbo:frame-load', this._onFrameLoad);
  }

  disconnect() {
    if (this._onFrameLoad) document.removeEventListener('turbo:frame-load', this._onFrameLoad);
  }

  // --- Progressive reveal (add .is-revealed; keep .section-locked for CSS compound selector) ---

  revealStage(stage) {
    const map = {
      duration:  this.hasSectionDurationTarget  ? this.sectionDurationTarget  : null,
      format:    this.hasSectionFormatTarget    ? this.sectionFormatTarget    : null,
      recipient: this.hasSectionRecipientTarget ? this.sectionRecipientTarget : null,
      message:   this.hasSectionMessageTarget   ? this.sectionMessageTarget   : null
    };
    const el = map[stage];
    if (el) el.classList.add('is-revealed');
  }

  // --- Duration slider ---

  selectPresetDuration() {
    // Picking a preset clears the custom-toggle radio + hides the slider so
    // the two "radio groups" behave as one mutually-exclusive set.
    if (this.hasCustomToggleTarget) this.customToggleTarget.checked = false;
    if (this.hasSliderWrapTarget) this.sliderWrapTarget.hidden = true;
    if (this.hasCustomCouponIdTarget) this.customCouponIdTarget.value = '';
    this.updateRecap();
  }

  toggleCustomDuration(event) {
    const on = event.target.checked;
    this.sliderWrapTarget.hidden = !on;
    if (on) {
      this.couponRadioTargets.forEach(r => { r.checked = false; });
      this.updateSlider();
    } else {
      this.customCouponIdTarget.value = '';
    }
    this.updateRecap();
  }

  updateSlider() {
    const months = Number(this.rangeInputTarget.value);
    if (this.hasMonthLabelTarget) this.monthLabelTarget.textContent = months;
    const id = this.couponIdsValue[months];
    this.customCouponIdTarget.value = id != null ? id : '';
    this.updateRecap();
  }

  // --- Format ---

  toggleFormatLevel() {
    const mode = this._selectedValue(this.formatModeTargets);
    const physical = mode === 'physical';
    this.physicalWrapTarget.hidden = !physical;
    this._syncRecipientTitles(physical);
    // Brief 2.2: physical opens the grid but does NOT pick a delivery; section 4
    // stays hidden until user clicks an écrin radio. Email reveals section 4 right away.
    if (!physical) {
      this.deliveryRadioTargets.forEach(r => { r.checked = false; });
    }
    this._resetMessageAndRecipient();
    this._syncRecipientFields();
    this._clearAllErrors();
    this.updateRecap();
  }

  _resetMessageAndRecipient() {
    if (this.hasNoteTarget) {
      this.noteTarget.value = this.noteTarget.defaultValue || '';
      this.noteTarget.dataset.engaged = 'false';
    }
    if (this.hasSenderNameTarget) {
      this.senderNameTarget.value = this.senderNameTarget.defaultValue || '';
    }
    if (this.hasRecipientFirstNameTarget) {
      this.recipientFirstNameTarget.value = this.recipientFirstNameTarget.defaultValue || '';
    }
    this.updateCharCount();

    this.recipientModeTargets.forEach(r => { r.checked = r.value === 'direct'; });

    const wraps = [
      this.hasFieldsDirectMailTarget     && this.fieldsDirectMailTarget,
      this.hasFieldsSelfMailTarget       && this.fieldsSelfMailTarget,
      this.hasFieldsDirectPhysicalTarget && this.fieldsDirectPhysicalTarget,
      this.hasFieldsSelfPhysicalTarget   && this.fieldsSelfPhysicalTarget
    ].filter(Boolean);

    wraps.forEach(wrap => {
      wrap.querySelectorAll('input, textarea, select').forEach(el => {
        if (el.type === 'radio' || el.type === 'checkbox' || el.type === 'hidden') return;
        el.value = el.defaultValue || '';
      });
    });

    if (this.hasSectionRecipientTarget) {
      this.sectionRecipientTarget.classList.remove('is-revealed');
    }
  }

  // --- Recipient ---

  toggleRecipientMode() {
    this._syncRecipientFields();
    this._clearAllErrors();
    this.validate();
  }

  _syncRecipientFields() {
    const formatMode = this._selectedValue(this.formatModeTargets);
    const recipientMode = this._selectedValue(this.recipientModeTargets);
    const physical = formatMode === 'physical';
    const fields = {
      directMail:     !physical && recipientMode === 'direct',
      selfMail:       !physical && recipientMode === 'self',
      directPhysical:  physical && recipientMode === 'direct',
      selfPhysical:    physical && recipientMode === 'self'
    };
    if (this.hasFieldsDirectMailTarget)     this.fieldsDirectMailTarget.hidden     = !fields.directMail;
    if (this.hasFieldsSelfMailTarget)       this.fieldsSelfMailTarget.hidden       = !fields.selfMail;
    if (this.hasFieldsDirectPhysicalTarget) this.fieldsDirectPhysicalTarget.hidden = !fields.directPhysical;
    if (this.hasFieldsSelfPhysicalTarget)   this.fieldsSelfPhysicalTarget.hidden   = !fields.selfPhysical;
    this._syncRecipientTitles(physical);
  }

  _syncRecipientTitles(physical) {
    if (this.hasRecipientTitleDirectTarget) {
      this.recipientTitleDirectTarget.textContent = physical
        ? "À l'adresse postale de mon proche"
        : "À l'adresse email de mon proche";
    }
    if (this.hasRecipientTitleSelfTarget) {
      this.recipientTitleSelfTarget.textContent = physical
        ? 'À mon adresse postale'
        : 'À mon adresse email';
    }
  }

  // --- Char count ---

  updateCharCount() {
    if (!this.hasNoteTarget || !this.hasCharCountTarget) return;
    const len = this.noteTarget.value.length;
    this.charCountTarget.textContent = len;
    const valid = len >= 10;
    if (this.hasCharStatusTarget) {
      if (len === 0) {
        this.charStatusTarget.textContent = 'Minimum 10 caractères';
      } else if (len < 10) {
        this.charStatusTarget.textContent = `Minimum 10 caractères (${10 - len} restant${10 - len > 1 ? 's' : ''})`;
      } else {
        this.charStatusTarget.textContent = '✓ Merci, c\'est parfait';
      }
    }
    // Counter color states (brief 3.1)
    const wrap = this.noteTarget.closest('.note-wrap');
    const counter = wrap && wrap.querySelector('.note-counter');
    if (counter) {
      counter.classList.toggle('below-min', !valid);
      counter.classList.toggle('valid', valid);
    }
    this.noteTarget.classList.toggle('is-invalid', len > 0 && !valid);
    this.validate();
  }

  // --- Recap Turbo Frame ---

  updateRecap() {
    const params = new URLSearchParams();
    if (this.countrySelectTarget.value) params.set('country_id',  this.countrySelectTarget.value);
    if (this._selectedCouponId())       params.set('coupon_id',   this._selectedCouponId());
    if (this._recapDeliveryId())        params.set('delivery_id', this._recapDeliveryId());

    const url = `${this.recapUrlValue}?${params.toString()}`;
    const frame = document.getElementById('price_recap');
    if (frame) frame.setAttribute('src', url);

    this._progressReveal();
    this.validate();
  }

  _progressReveal() {
    // Section 2 (duration) is always visible after country select — reveal it immediately
    this.revealStage('duration');
    if (this._selectedCouponId()) this.revealStage('format');

    // Section 4 (message) reveal (brief 2.2):
    //   email → reveal as soon as the email mode is picked
    //   physical → reveal only after user picks an écrin radio
    const formatMode = this._selectedValue(this.formatModeTargets);
    const physical = formatMode === 'physical';
    const deliveryPicked = this.deliveryRadioTargets.some(r => r.checked);
    const messageReady = formatMode && (!physical || deliveryPicked);
    if (messageReady) this.revealStage('message');

    // Section 5 (recipient) reveals only after user engages with the note textarea
    if (messageReady && this.hasNoteTarget && this.noteTarget.dataset.engaged === 'true') {
      this.revealStage('recipient');
    }
  }

  revealRecipientOnNoteFocus() {
    if (!this.hasNoteTarget) return;
    // Focus set by runValidationAndFocus() must not count as engagement —
    // it would auto-reveal step 5 while step 4 is the one being corrected.
    if (this._programmaticFocus) return;
    this.noteTarget.dataset.engaged = 'true';
    this._progressReveal();
  }

  syncAddressCountry() {
    const opt = this.countrySelectTarget.selectedOptions[0];
    const code = opt && opt.dataset ? (opt.dataset.code || '').toLowerCase() : '';
    if (!code) return;

    [
      this.hasFieldsDirectPhysicalTarget && this.fieldsDirectPhysicalTarget,
      this.hasFieldsSelfPhysicalTarget   && this.fieldsSelfPhysicalTarget
    ].filter(Boolean).forEach(wrap => {
      wrap.setAttribute('data-address-form-locked-country-code-value', code);
    });
  }

  // --- Validate + submit label ---

  // The submit buttons are never disabled (disabled buttons hide *why* the
  // form is blocked — Baymard). validate() only refreshes the price label and
  // clears inline errors that became valid as the user types.
  validate() {
    const buttons = [
      ...this.submitBtnDesktopTargets,
      ...this.submitBtnMobileTargets
    ];
    const total = this._renderedTotal();
    buttons.forEach(btn => {
      btn.textContent = total ? `Commander · ${total}` : 'Commander';
    });
    this._clearResolvedErrors();
    if (this._selectedDeliveryId()) this._clearHintSection3();
  }

  // Bound to `click` on the submit buttons (not the form's submit event):
  // Turbo Drive captures `submit` at the document level before it bubbles to
  // the form, so preventDefault there would be too late to stop the visit.
  // Cancelling the click prevents the native form submission altogether.
  onSubmit(event) {
    if (this._isValid()) return;
    event.preventDefault();
    this.runValidationAndFocus();
  }

  // Show a field's error when the user leaves it empty/invalid (focusout
  // bubbles from every field inside the form element).
  onFieldBlur(event) {
    const fields = this._requiredFields();
    const field = fields.find(f => f.el === event.target);
    if (!field) return;
    if (field.valid()) {
      this._clearErr(field.el);
    } else {
      this._showErr(field.el, field.message);
    }
  }

  // Ordered guidance: surface ONLY the first incomplete step (3 → 4 → 5),
  // never revealing or validating the steps after it.
  runValidationAndFocus() {
    this._clearAllErrors();

    // Step 2 — no duration/coupon selected (custom toggle on with no slider value)
    if (!this.countrySelectTarget.value || !this._selectedCouponId()) {
      const section = this.hasSectionDurationTarget ? this.sectionDurationTarget : this.element;
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Step 3 — delivery mode ("Par email" / écrin physique) not chosen yet
    if (!this._selectedDeliveryId()) {
      this._hintSection3();
      return;
    }

    // Step 4 — note + signature
    const messageFields = this._requiredFields().filter(f => f.step === 'message');
    const invalidMessage = messageFields.filter(f => !f.valid());
    if (invalidMessage.length > 0) {
      this.revealStage('message');
      invalidMessage.forEach(f => this._showErr(f.el, f.message));
      this._focusField(invalidMessage[0].el);
      return;
    }

    // Step 5 — recipient details (step 4 is complete, so revealing is allowed)
    if (this.hasNoteTarget) this.noteTarget.dataset.engaged = 'true';
    this.revealStage('recipient');
    this._syncRecipientFields();
    const recipientFields = this._requiredFields().filter(f => f.step === 'recipient');
    const invalidRecipient = recipientFields.filter(f => !f.valid());
    if (invalidRecipient.length > 0) {
      invalidRecipient.forEach(f => this._showErr(f.el, f.message));
      this._focusField(invalidRecipient[0].el);
    }
  }

  // Single source of truth for "what is required right now", in DOM order.
  // Depends on the selected format mode (mail/physical) and recipient mode.
  _requiredFields() {
    const fields = [];
    const push = (present, el, valid, message, step) => {
      if (present && el) fields.push({ el, valid, message, step });
    };
    const filled = el => () => el.value.trim() !== '';

    push(this.hasNoteTarget, this.hasNoteTarget && this.noteTarget,
      () => this.noteTarget.value.length >= 10,
      'Votre petit mot doit contenir au moins 10 caractères.', 'message');
    push(this.hasRecipientFirstNameTarget, this.hasRecipientFirstNameTarget && this.recipientFirstNameTarget,
      this.hasRecipientFirstNameTarget && filled(this.recipientFirstNameTarget),
      'Indiquez le prénom du destinataire.', 'message');
    push(this.hasSenderNameTarget, this.hasSenderNameTarget && this.senderNameTarget,
      this.hasSenderNameTarget && filled(this.senderNameTarget),
      'Indiquez votre prénom.', 'message');

    const physical = this._selectedValue(this.formatModeTargets) === 'physical';
    const recipientMode = this._selectedValue(this.recipientModeTargets);

    if (!physical && recipientMode === 'direct') {
      push(this.hasRecipientEmailTarget, this.hasRecipientEmailTarget && this.recipientEmailTarget,
        () => this._emailValid(this.recipientEmailTarget.value),
        'Indiquez une adresse email valide.', 'recipient');
    } else if (!physical && recipientMode === 'self') {
      push(this.hasSelfEmailTarget, this.hasSelfEmailTarget && this.selfEmailTarget,
        () => this._emailValid(this.selfEmailTarget.value),
        'Indiquez une adresse email valide.', 'recipient');
    } else if (physical && recipientMode === 'direct') {
      push(this.hasRecipientNameTarget, this.hasRecipientNameTarget && this.recipientNameTarget,
        this.hasRecipientNameTarget && filled(this.recipientNameTarget),
        'Indiquez le prénom et le nom du destinataire.', 'recipient');
      this._pushAddressFields(fields, {
        street: this.streetAddressTargets[0], postal: this.postalCodeTargets[0],
        city: this.cityTargets[0], phone: this.phoneTargets[0]
      });
    } else if (physical && recipientMode === 'self') {
      push(true, this.selfFullNameTargets[0],
        this.selfFullNameTargets[0] && filled(this.selfFullNameTargets[0]),
        'Indiquez votre prénom et votre nom.', 'recipient');
      this._pushAddressFields(fields, {
        street: this.selfStreetAddressTargets[0], postal: this.selfPostalCodeTargets[0],
        city: this.selfCityTargets[0], phone: this.selfPhoneTargets[0]
      });
    }
    return fields;
  }

  _pushAddressFields(fields, els) {
    const filled = el => () => el.value.trim() !== '';
    if (els.street) fields.push({ el: els.street, valid: filled(els.street), message: 'Indiquez l’adresse.', step: 'recipient' });
    if (els.postal) fields.push({ el: els.postal, valid: filled(els.postal), message: 'Indiquez le code postal.', step: 'recipient' });
    if (els.city)   fields.push({ el: els.city,   valid: filled(els.city),   message: 'Indiquez la ville.', step: 'recipient' });
    if (els.phone)  fields.push({ el: els.phone,  valid: () => this._phoneValid(els.phone), message: 'Indiquez un numéro de téléphone valide.', step: 'recipient' });
  }

  // --- Inline errors ---

  _showErr(el, message) {
    el.classList.add('cc-invalid');
    let anchor = el.closest('.iti') || el;
    let err = anchor.nextElementSibling;
    if (!err || !err.classList.contains('cc-field-err')) {
      err = document.createElement('p');
      err.className = 'cc-field-err';
      anchor.insertAdjacentElement('afterend', err);
    }
    err.textContent = message;
  }

  _clearErr(el) {
    el.classList.remove('cc-invalid');
    const anchor = el.closest('.iti') || el;
    const err = anchor.nextElementSibling;
    if (err && err.classList.contains('cc-field-err')) err.remove();
  }

  _clearAllErrors() {
    this.element.querySelectorAll('.cc-field-err').forEach(el => el.remove());
    this.element.querySelectorAll('.cc-invalid').forEach(el => el.classList.remove('cc-invalid'));
    this._clearHintSection3();
  }

  _clearResolvedErrors() {
    const fields = this._requiredFields();
    fields.forEach(f => {
      if (f.el.classList.contains('cc-invalid') && f.valid()) this._clearErr(f.el);
    });
  }

  _hintSection3() {
    const toggle = this.element.querySelector('.ecrin-toggle');
    if (!toggle) return;
    toggle.classList.add('cc-toggle-hint');
    let hint = toggle.nextElementSibling;
    if (!hint || !hint.classList.contains('cc-step-hint')) {
      hint = document.createElement('p');
      hint.className = 'cc-step-hint';
      toggle.insertAdjacentElement('afterend', hint);
    }
    hint.textContent = "Choisissez d'abord le mode d'envoi de la carte.";
    toggle.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  _clearHintSection3() {
    const toggle = this.element.querySelector('.ecrin-toggle');
    if (toggle) toggle.classList.remove('cc-toggle-hint');
    this.element.querySelectorAll('.cc-step-hint').forEach(el => el.remove());
  }

  _focusField(el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this._programmaticFocus = true;
    try {
      el.focus({ preventScroll: true });
    } finally {
      this._programmaticFocus = false;
    }
  }

  _isValid() {
    if (!this.countrySelectTarget.value) return false;
    if (!this._selectedCouponId()) return false;
    if (!this._selectedDeliveryId()) return false;
    if (!this.hasRecipientFirstNameTarget || !this.recipientFirstNameTarget.value.trim()) return false;
    if (!this.hasSenderNameTarget || !this.senderNameTarget.value.trim()) return false;
    if (!this.hasNoteTarget || this.noteTarget.value.length < 10) return false;

    const formatMode = this._selectedValue(this.formatModeTargets);
    const recipientMode = this._selectedValue(this.recipientModeTargets);
    if (!recipientMode) return false;

    const physical = formatMode === 'physical';
    if (!physical && recipientMode === 'direct') {
      if (!this.hasRecipientEmailTarget || !this._emailValid(this.recipientEmailTarget.value)) return false;
    } else if (!physical && recipientMode === 'self') {
      if (!this.hasSelfEmailTarget || !this._emailValid(this.selfEmailTarget.value)) return false;
    } else if (physical && recipientMode === 'direct') {
      if (!this.hasRecipientNameTarget || !this.recipientNameTarget.value.trim()) return false;
      if (!this._allFilled([this.streetAddressTargets, this.postalCodeTargets, this.cityTargets])) return false;
      if (!this._phoneValid(this.phoneTargets[0])) return false;
    } else if (physical && recipientMode === 'self') {
      if (!this._allFilled([this.selfFullNameTargets, this.selfStreetAddressTargets, this.selfPostalCodeTargets, this.selfCityTargets])) return false;
      if (!this._phoneValid(this.selfPhoneTargets[0])) return false;
    }
    return true;
  }

  _allFilled(targetGroups) {
    return targetGroups.every(group => group[0] && group[0].value.trim());
  }

  // Minimal email shape check — `local@domain.tld`. Backend re-validates,
  // this just gates the submit button against obvious typos.
  _emailValid(value) {
    const v = (value || '').trim();
    if (!v) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }

  _phoneValid(input) {
    if (!input || !input.value.trim()) return false;
    // Ask intl-tel-input directly — `isValidNumber()` reads the current value
    // each call, so the gate flips the moment the user types the last digit
    // (no need to wait for blur/change to clear `.has-error` on the wrapper).
    if (input.__iti && typeof input.__iti.isValidNumber === 'function') {
      // utils.js is loaded async; if it hasn't finished yet, isValidNumber
      // returns `null`. Treat that as "unknown" and fall back to the wrapper
      // signal + length floor below.
      var result = input.__iti.isValidNumber();
      if (result === true) return true;
      if (result === false) return false;
    }
    // Fallbacks for the brief window before utils.js arrives.
    var iti = input.closest('.iti');
    var wrapper = iti && iti.parentElement;
    if (wrapper && wrapper.classList.contains('has-error')) return false;
    return input.value.replace(/\D/g, '').length >= 6;
  }

  _selectedCouponId() {
    const preset = this.couponRadioTargets.find(r => r.checked);
    if (preset) return preset.value;
    if (this.hasCustomCouponIdTarget && this.customCouponIdTarget.value) {
      return this.customCouponIdTarget.value;
    }
    return '';
  }

  _selectedDeliveryId() {
    const mode = this._selectedValue(this.formatModeTargets);
    if (mode === 'mail') {
      return this.hasMailDeliveryIdTarget ? this.mailDeliveryIdTarget.value : '';
    }
    const radio = this.deliveryRadioTargets.find(r => r.checked);
    return radio ? radio.value : '';
  }

  // For the recap preview only: fall back to mail delivery when no format picked yet
  _recapDeliveryId() {
    const id = this._selectedDeliveryId();
    if (id) return id;
    return this.hasMailDeliveryIdTarget ? this.mailDeliveryIdTarget.value : '';
  }

  _selectedValue(targets) {
    const t = targets.find(r => r.checked);
    return t ? t.value : '';
  }

  _renderedTotal() {
    const el = document.querySelector('#price_recap .recap-total-value');
    return el ? el.textContent.trim() : '';
  }
}
